import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { DisconnectUserAction, LoadUserResourceAction, ShowToastMessageAction } from './app.actions';
import { Platform, ToastController } from '@ionic/angular';
import { LoadUserProfileAction, ResetUserProfileAction } from '../../user/state/user-profile.actions';
import { ResetAllAuthUserAction } from '../../user/state/auth.actions';
import { RemovePushTokenAction } from '../../user/state/user-notification.actions';
import { RemoveLocationAccessAction } from './user-location-access.action';
import { THE_ACCOUNT_WAS_JUST_DELETED, WHERE_A_DELETION_LANDS } from './a-deleted-account-says-goodbye';
import { LocationAccessService } from '../../user/services/location-access.service';
import { lastValueFrom } from 'rxjs';
import { FirebaseService } from '@smatchy/ionic-auth';
import { UsageMeasurementService } from '../../../services/usage-measurement.service';

const notificationsAskedKey = 'notificationsAsked';
const localAuthKeys = ['pendingEmailSignup', 'onboardingDraft', 'lastRoute'];

export type AppStateModel = object;

const defaults: AppStateModel = {};

@State<AppStateModel>({
  name: 'app',
  defaults,
})
@Injectable()
export class AppState {
  constructor(
    public readonly _platform: Platform,
    private readonly _toastController: ToastController,
    private readonly _locationAccessService: LocationAccessService,
    private readonly _firebaseService: FirebaseService,
    private readonly _usageMeasurement: UsageMeasurementService,
  ) {}

  @Action(LoadUserResourceAction)
  async loadUserResourceAction({ dispatch }: StateContext<AppStateModel>) {
    dispatch(new LoadUserProfileAction());
  }

  @Action(DisconnectUserAction)
  async disconnectUserAction(
    { dispatch }: StateContext<AppStateModel>,
    { theAccountIsAlreadyGone }: DisconnectUserAction,
  ) {
    /*
     * ASKING THE SERVER TO FORGET THINGS IT HAS ALREADY FORGOTTEN. Both calls
     * below are authenticated. After a deletion the account is gone, so they
     * answer 401, the interceptor renews the token, Firebase answers that the
     * user record does not exist, and the failure of our own tidying up is
     * reported as a production error. See the action for the full sequence.
     */
    if (!theAccountIsAlreadyGone) {
      try {
        await lastValueFrom(dispatch(new RemovePushTokenAction()), {
          defaultValue: undefined,
        });
      } catch {
        // Still disconnect if action fails
      }
      try {
        await lastValueFrom(dispatch(new RemoveLocationAccessAction()), {
          defaultValue: undefined,
        });
      } catch {
        // Still disconnect if action fails
      }
    }
    try {
      await this._firebaseService.signOut();
    } catch {
      // Local auth state must still be cleared if Firebase sign-out fails
    }
    await lastValueFrom(dispatch([new ResetAllAuthUserAction(), new ResetUserProfileAction()]), {
      defaultValue: undefined,
    });
    /*
     * THE ANALYTICS IDENTITY, which this handler used to leave behind. It
     * revoked the push token, signed Firebase out and purged the stored state,
     * and never touched FirebaseAnalytics: on a shared phone the next person's
     * events were attributed to the account that had just left. Found on
     * 25 September 2026.
     */
    try {
      await this._usageMeasurement.forgetWhoThisWas();
    } catch {
      // Wrapped like every other step of this handler: the service swallows its
      // own failures today, and a sign-out must not start depending on that.
    }
    localStorage.removeItem('userProfile');
    localStorage.removeItem(notificationsAskedKey);
    localAuthKeys.forEach((key) => localStorage.removeItem(key));
    /*
     * THE REFUSAL OF THE MEASUREMENT IS NOT REMOVED HERE, and must not be:
     * somebody who objected does not re-consent by signing out. The account
     * carries it since 15 October 2026, and this copy is what keeps the device
     * quiet until the next person signs in and their account answers. The
     * named lists above are what makes that work, rather than a blanket clear;
     * a test holds it.
     */
    // 'pendingEmailSignup' stays in the list above so that a password left by an
    // earlier version is erased on sign-out too, not only at start-up. Nothing
    // writes it any more: the account is created as soon as the password is
    // confirmed, so no credentials are carried between screens at all.
    await this._locationAccessService.clearPersistedStateOnLogout();
    sessionStorage.clear();
    if (theAccountIsAlreadyGone) {
      sessionStorage.setItem(THE_ACCOUNT_WAS_JUST_DELETED, 'true');
      this.leaveForTheFirstScreen();
      return;
    }
    this.reloadApp();
  }

  @Action(ShowToastMessageAction)
  async showToastErrorAction(
    _: StateContext<AppStateModel>,
    { message, icon, duration, position }: ShowToastMessageAction,
  ) {
    const toast = await this._toastController.create({
      message: message || 'Success',
      duration: duration || 1500,
      icon: icon || 'checkmark-outline',
      position: position ?? 'top',
    });

    await toast.present();
  }

  private reloadApp(): void {
    window.location.reload();
  }

  /** A reload too, on the first screen rather than on the one somebody was on. */
  private leaveForTheFirstScreen(): void {
    window.location.replace(WHERE_A_DELETION_LANDS);
  }
}
